/**
 * WeaponPanelRex - Panel lateral de selección de armas usando Rex UI
 *
 * Un panel que se desliza desde la derecha mostrando el loadout
 * del jugador con slots de armas seleccionables.
 */

import { Scene, GameObjects } from 'phaser';
import UIPlugin from 'phaser3-rex-plugins/templates/ui/ui-plugin.js';
import { audioManager } from '../core/CoreManagerAudio';
import { LayerDepths } from '../constants/enums';
import { t } from '../i18n/Localization';

interface WeaponSlot {
  id: 'primary' | 'secondary' | 'special';
  name: string;
  key: string;
  equipped: string | null;
  equipmentName: string;
}

export interface WeaponPanelRexConfig {
  scene: Scene;
  width: number;
  onStart: (levelId: number, weapons: WeaponSlot[]) => void;
  onClose: () => void;
}

// Colors
const COLORS = {
  PANEL_BG: 0x1a1a2e,
  SLOT_BG: 0x2d2d44,
  SLOT_BORDER: 0x4a9c6d,
  SLOT_BORDER_EMPTY: 0x555555,
  ACCENT: 0x4CAF50,
  ACCENT_HOVER: 0x5cbf60,
  ACCENT_PRESSED: 0x3d8b40,
  TEXT_PRIMARY: '#ffffff',
  TEXT_SECONDARY: '#4CAF50',
  TEXT_MUTED: '#666666',
  OVERLAY: 0x000000,
};

export class WeaponPanelRex {
  private scene: Scene;
  private rexUI: UIPlugin;
  private container: GameObjects.Container;
  private panelSizer: any; // Rex Sizer
  private panelWidth: number;
  private panelHeight: number;
  private isVisible: boolean = false;
  private selectedLevel: number = 1;
  private scaleRatio: number = 1;
  private onStart: (levelId: number, weapons: WeaponSlot[]) => void;
  private onClose: () => void;

  // Overlay for clicking outside
  private overlay: GameObjects.Rectangle | null = null;

  // Weapon slots
  private weapons: WeaponSlot[] = [
    { id: 'primary', name: 'Primary', key: 'Z', equipped: 'laserSimple', equipmentName: 'Laser Simple' },
    { id: 'secondary', name: 'Secondary', key: 'X', equipped: null, equipmentName: '(empty)' },
    { id: 'special', name: 'Special', key: 'C', equipped: 'tractorPuny', equipmentName: 'Tractor Puny' },
  ];

  // UI elements
  private levelText: GameObjects.Text | null = null;
  private slotButtons: any[] = [];

  constructor(config: WeaponPanelRexConfig) {
    this.scene = config.scene;
    this.rexUI = (config.scene as any).rexUI as UIPlugin;
    this.panelWidth = config.width;
    this.onStart = config.onStart;
    this.onClose = config.onClose;

    const { height } = this.scene.scale;
    this.panelHeight = height;
    this.scaleRatio = Math.min(this.scene.scale.width / 1600, height / 900);

    this.createOverlay();
    this.createPanel();
  }

  private createOverlay(): void {
    const { width, height } = this.scene.scale;

    this.overlay = this.scene.add.rectangle(width / 2, height / 2, width, height, COLORS.OVERLAY, 0);
    this.overlay.setDepth(LayerDepths.WeaponPanelOverlay);
    this.overlay.setInteractive();
    this.overlay.setVisible(false);
    this.overlay.on('pointerdown', () => this.hide());
  }

  private createPanel(): void {
    const { width, height } = this.scene.scale;

    // Container starts off-screen to the right
    this.container = this.scene.add.container(width + this.panelWidth / 2, 0);
    this.container.setDepth(LayerDepths.WeaponPanelContainer);

    // Main panel sizer
    this.panelSizer = this.rexUI.add.sizer({
      x: 0,
      y: height / 2,
      width: this.panelWidth,
      height: this.panelHeight,
      orientation: 'y',
      space: { left: 20, right: 20, top: 40, bottom: 40, item: 20 }
    });

    // Background
    this.panelSizer.addBackground(
      this.rexUI.add.roundRectangle(0, 0, 0, 0, { tl: 12, bl: 12, tr: 1, br: 1 }, COLORS.PANEL_BG, 0.98)
        .setStrokeStyle(3, COLORS.SLOT_BORDER)
    );

    // Close button (top-left)
    this.createCloseButton();

    // Title
    const title = this.scene.add.text(0, 0, t('weapon_panel.title'), {
      fontFamily: 'Crackvetica, Arial Black',
      fontSize: `${32 * this.scaleRatio}px`,
      color: COLORS.TEXT_SECONDARY,
    }).setOrigin(0.5);
    this.panelSizer.add(title, { align: 'center', padding: { top: 20 } });

    // Level indicator
    this.levelText = this.scene.add.text(0, 0, t('weapon_panel.level').replace('{level}', '1'), {
      fontFamily: 'Crackvetica, Arial',
      fontSize: `${22 * this.scaleRatio}px`,
      color: COLORS.TEXT_PRIMARY,
    }).setOrigin(0.5);
    this.panelSizer.add(this.levelText, { align: 'center' });

    // Spacer
    this.panelSizer.addSpace();

    // Weapon slots container
    const slotsContainer = this.createWeaponSlots();
    this.panelSizer.add(slotsContainer, { align: 'center' });

    // Spacer
    this.panelSizer.addSpace();

    // START button
    const startButton = this.createStartButton();
    this.panelSizer.add(startButton, { align: 'center', padding: { bottom: 40 } });

    // Layout
    this.panelSizer.layout();

    // Add to container
    this.container.add(this.panelSizer);
  }

  private createCloseButton(): void {
    const closeBtn = this.scene.add.text(
      -this.panelWidth / 2 + 35 * this.scaleRatio,
      -this.panelHeight / 2 + 35 * this.scaleRatio,
      '✕',
      {
        fontFamily: 'Arial',
        fontSize: `${28 * this.scaleRatio}px`,
        color: '#888888',
      }
    ).setOrigin(0.5);

    closeBtn.setInteractive({ useHandCursor: true });

    closeBtn.on('pointerover', () => {
      closeBtn.setColor('#ffffff');
      closeBtn.setScale(1.1);
      this.playSound('hover');
    });

    closeBtn.on('pointerout', () => {
      closeBtn.setColor('#888888');
      closeBtn.setScale(1);
    });

    closeBtn.on('pointerdown', () => {
      this.playSound('click');
      this.hide();
    });

    this.container.add(closeBtn);
  }

  private createWeaponSlots(): any {
    const slotWidth = this.panelWidth * 0.85;
    const slotHeight = 85 * this.scaleRatio;

    // Vertical sizer for all slots
    const slotsSizer = this.rexUI.add.sizer({
      orientation: 'y',
      space: { item: 15 }
    });

    this.weapons.forEach((weapon) => {
      const slot = this.createWeaponSlot(slotWidth, slotHeight, weapon);
      slotsSizer.add(slot, { align: 'center' });
      this.slotButtons.push(slot);
    });

    slotsSizer.layout();
    return slotsSizer;
  }

  private createWeaponSlot(width: number, height: number, weapon: WeaponSlot): any {
    const hasWeapon = weapon.equipped !== null;

    // Create slot using Rex Label
    const slot = this.rexUI.add.label({
      width: width,
      height: height,
      orientation: 'y',
      background: this.rexUI.add.roundRectangle(0, 0, 0, 0, 8, COLORS.SLOT_BG)
        .setStrokeStyle(2, hasWeapon ? COLORS.SLOT_BORDER : COLORS.SLOT_BORDER_EMPTY),

      text: this.scene.add.text(0, 0, hasWeapon ? weapon.equipmentName : t('ui.empty'), {
        fontFamily: 'Crackvetica, Arial',
        fontSize: `${20 * this.scaleRatio}px`,
        color: hasWeapon ? COLORS.TEXT_PRIMARY : COLORS.TEXT_MUTED,
      }),

      icon: this.createSlotHeader(weapon),

      space: { left: 15, right: 15, top: 10, bottom: 10, icon: 8 },
      align: 'left'
    });

    // Make interactive
    slot.setInteractive({ useHandCursor: true });

    slot.on('pointerover', () => {
      const bg = slot.getElement('background') as any;
      if (bg) bg.setFillStyle(0x3d3d54);
      this.playSound('hover');
    });

    slot.on('pointerout', () => {
      const bg = slot.getElement('background') as any;
      if (bg) bg.setFillStyle(COLORS.SLOT_BG);
    });

    slot.on('pointerdown', () => {
      // TODO: Open weapon selection for this slot
      this.playSound('click');
      console.log(`Selected slot: ${weapon.id}`);
    });

    slot.layout();
    return slot;
  }

  private createSlotHeader(weapon: WeaponSlot): GameObjects.Text {
    const rawLabel = t(`slot.${weapon.id}`);
    const slotLabel = rawLabel.startsWith('slot.') ? weapon.name : rawLabel;
    return this.scene.add.text(0, 0, `${slotLabel} [${weapon.key}]`, {
      fontFamily: 'Crackvetica, Arial Black',
      fontSize: `${14 * this.scaleRatio}px`,
      color: COLORS.TEXT_SECONDARY,
    });
  }

  private createStartButton(): any {
    const btnWidth = this.panelWidth * 0.75;
    const btnHeight = 55 * this.scaleRatio;

    const startButton = this.rexUI.add.label({
      width: btnWidth,
      height: btnHeight,
      background: this.rexUI.add.roundRectangle(0, 0, 0, 0, 10, COLORS.ACCENT)
        .setStrokeStyle(3, COLORS.ACCENT_PRESSED),
      text: this.scene.add.text(0, 0, t('weapon_panel.start_mission'), {
        fontFamily: 'Crackvetica, Arial Black',
        fontSize: `${24 * this.scaleRatio}px`,
        color: COLORS.TEXT_PRIMARY,
      }),
      space: { left: 20, right: 20, top: 10, bottom: 10 },
      align: 'center'
    });

    startButton.setInteractive({ useHandCursor: true });

    startButton.on('pointerover', () => {
      const bg = startButton.getElement('background') as any;
      if (bg) bg.setFillStyle(COLORS.ACCENT_HOVER);
      startButton.setScale(1.02);
      this.playSound('hover');
    });

    startButton.on('pointerout', () => {
      const bg = startButton.getElement('background') as any;
      if (bg) bg.setFillStyle(COLORS.ACCENT);
      startButton.setScale(1);
    });

    startButton.on('pointerdown', () => {
      const bg = startButton.getElement('background') as any;
      if (bg) bg.setFillStyle(COLORS.ACCENT_PRESSED);
      this.playSound('click');
    });

    startButton.on('pointerup', () => {
      const bg = startButton.getElement('background') as any;
      if (bg) bg.setFillStyle(COLORS.ACCENT_HOVER);
      this.onStart(this.selectedLevel, this.weapons);
    });

    startButton.layout();
    return startButton;
  }

  private playSound(type: 'hover' | 'click'): void {
    audioManager.playUISound(type, this.scene);
  }

  show(levelId: number): void {
    if (this.isVisible) return;

    this.selectedLevel = levelId;
    this.isVisible = true;

    // Update level text
    if (this.levelText) {
      this.levelText.setText(t('weapon_panel.level').replace('{level}', `${levelId}`));
    }

    // Show overlay with fade
    if (this.overlay) {
      this.overlay.setVisible(true);
      this.overlay.setAlpha(0);
      this.scene.tweens.add({
        targets: this.overlay,
        alpha: 0.5,
        duration: 200,
        ease: 'Power2',
      });
    }

    // Slide in animation
    const { width } = this.scene.scale;
    this.scene.tweens.add({
      targets: this.container,
      x: width - this.panelWidth / 2,
      duration: 300,
      ease: 'Back.easeOut',
    });
  }

  hide(): void {
    if (!this.isVisible) return;

    this.isVisible = false;

    // Fade out overlay
    if (this.overlay) {
      this.scene.tweens.add({
        targets: this.overlay,
        alpha: 0,
        duration: 200,
        ease: 'Power2',
        onComplete: () => {
          this.overlay?.setVisible(false);
        },
      });
    }

    // Slide out animation
    const { width } = this.scene.scale;
    this.scene.tweens.add({
      targets: this.container,
      x: width + this.panelWidth / 2,
      duration: 250,
      ease: 'Power2',
      onComplete: () => {
        this.onClose();
      },
    });
  }

  isOpen(): boolean {
    return this.isVisible;
  }

  setWeapons(weapons: WeaponSlot[]): void {
    this.weapons = weapons;
    // TODO: Rebuild slots if needed
  }

  getWeapons(): WeaponSlot[] {
    return this.weapons;
  }

  destroy(): void {
    // Kill tweens
    if (this.container) {
      this.scene.tweens.killTweensOf(this.container);
    }
    if (this.overlay) {
      this.scene.tweens.killTweensOf(this.overlay);
    }

    // Destroy Rex UI elements
    this.slotButtons.forEach(btn => btn.destroy());
    this.slotButtons = [];

    if (this.panelSizer) {
      this.panelSizer.destroy();
      this.panelSizer = null;
    }

    this.overlay?.destroy();
    this.container?.destroy();
  }
}
